/**
 * Logging Types
 *
 * Types for the structured logging system.
 */

/**
 * Log levels in order of severity (lowest to highest)
 * Using numeric values for efficient comparison
 */
export const LogLevel = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
  SILENT: 4,
} as const;

export type LogLevel = (typeof LogLevel)[keyof typeof LogLevel];

/**
 * Mapping from numeric log level values to their string names
 */
const LOG_LEVEL_NAMES: Record<LogLevel, string> = {
  [LogLevel.DEBUG]: "DEBUG",
  [LogLevel.INFO]: "INFO",
  [LogLevel.WARN]: "WARN",
  [LogLevel.ERROR]: "ERROR",
  [LogLevel.SILENT]: "SILENT",
};

/**
 * Helper function to convert LogLevel value to string name
 */
export function getLogLevelName(level: LogLevel): string {
  return LOG_LEVEL_NAMES[level] || "UNKNOWN";
}

/**
 * Configuration for the logging system
 */
export interface LoggingConfig {
  /**
   * Minimum log level to output
   * Logs below this level will be suppressed
   */
  minLevel: LogLevel;

  /**
   * Whether to include timestamps in log output
   */
  includeTimestamp?: boolean;

  /**
   * Whether to include the log level in output
   */
  includeLevel?: boolean;
}

/**
 * Context metadata that can be attached to log messages
 */
export interface LogContext {
  [key: string]: unknown;
}

/**
 * A log entry before being output
 */
export interface LogEntry {
  level: LogLevel;
  message: string;
  context?: LogContext;
  timestamp: Date;
}
